import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Close from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import {
	AddFaq,
	InformationQaOverviewDTO,
} from '@/api/DTO/information/faq.interface';
import {
	useCreateFaqMutation,
	useDeleteFaqMutation,
	useUpdateFaqMutation,
} from '@/api/information/faqApiSlice';
import { FormType } from '@/api/enum/formType.enum';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
	useCreateTagMutation,
	useUpdateTagMutation,
} from '@/api/exercise/unitTagApiSlice';
import { RequestDto } from '@/api/DTO/exercise/unitTag.interface';

interface Props {
	type: FormType;
	row?: RequestDto;
}

export default function AddTagDialog(props: Props) {
	const { type, row } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [createTag] = useCreateTagMutation();
	const [updateTag] = useUpdateTagMutation();
	const showModal = useModal();

	const defaultValues: DefaultValues<RequestDto> = row ? row : {};

	const formSchema = Yup.object().shape({
		nameZh: Yup.string().required(t('errorMessage.required')),
	});

	useEffect(() => {
		if (row) {
			methods.reset(row);
		}
	}, [row]);

	const methods = useForm<RequestDto>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<RequestDto> = async (data) => {
		const response =
			type == FormType.create ? await createTag(data) : await updateTag(data);
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createPageSuccess')
					: t('snackbar.editPageSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	return (
		<>
			{type == FormType.create && (
				<Button
					endIcon={<AddCircleOutlineIcon />}
					onClick={() => {
						setOpen(true);
					}}
					sx={{ marginRight: '24px' }}
					variant='contained'
				>
					{t('exerciseSetting.addTag')}
				</Button>
			)}
			{type == FormType.edit && (
				<IconButton
					color='primary'
					onClick={() => {
						setOpen(true);
					}}
				>
					<CreateOutlinedIcon />
				</IconButton>
			)}

			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{type == FormType.create
						? t('exerciseSetting.addTag')
						: t('exerciseSetting.changeTag')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='nameZh'
										label={t('exerciseSetting.tagTextZh')}
										maxLength={20}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='nameEn'
										label={t('exerciseSetting.tagTextEn_')}
										maxLength={100}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.isConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
